import React from 'react';
import { H5 } from '../../../../AbstractElements';
import { FormGroup, Input, Label, FormFeedback, Row, Col, InputGroup } from 'reactstrap';
import PropTypes from 'prop-types';
import { getFieldAccess } from '../../../../_helper/fieldAccess';
import { validateTcid, validateTaxNumber, normalizeFieldValue, REGEX, validateLicensePlate } from '../../../../_helper/regexHandler';

const CustomerInfoForm = ({ callFormState, formData, errors, handleInputChange, fieldPermissions = [], canEdit = true }) => {

    const agentAccess = getFieldAccess(fieldPermissions, 'customers.agent', canEdit);
    const createdAccess = getFieldAccess(fieldPermissions, 'customers.created', canEdit);
    const clientNameAccess = getFieldAccess(fieldPermissions, 'customers.clientname', canEdit);
    const tcidAccess = getFieldAccess(fieldPermissions, 'customers.identitynumber', canEdit);
    const companyNameAccess = getFieldAccess(fieldPermissions, 'customers.companyname', canEdit);
    const crsNumberAccess = getFieldAccess(fieldPermissions, 'customers.crsnumber', canEdit);
    const taxNumberAccess = getFieldAccess(fieldPermissions, 'customers.taxnumber', canEdit);
    const referenceAccess = getFieldAccess(fieldPermissions, 'customers.reference', canEdit);
    const licensePlateAccess = getFieldAccess(fieldPermissions, 'licenseplates.licenseplatenumber', canEdit);
    const accidentDateAccess = getFieldAccess(fieldPermissions, 'accidents.accidentdate', canEdit);

    return (
        <>
            <H5>Müvekkil Bilgileri</H5>
            <div style={{ height: "20px" }}></div>

            <Row>
                <Col md={6}>
                    <FormGroup>
                        <Label for="recipientOfPowerOfAttorney">
                            Vekaleti Alan {agentAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="text"
                            id="recipientOfPowerOfAttorney"
                            name="recipientOfPowerOfAttorney"
                            value={formData.customers?.[0]?.agent 
                                ? `${formData.customers[0].agent.firstName || ''} ${formData.customers[0].agent.lastName || ''}`.trim()
                                : formData.recipientOfPowerOfAttorney || ''}
                            placeholder="Vekaleti alan otomatik olarak atanıyor."
                            disabled={true}
                            invalid={!!errors.recipientOfPowerOfAttorney}
                        />
                        <FormFeedback>
                            {errors.recipientOfPowerOfAttorney}
                        </FormFeedback>
                    </FormGroup>
                </Col>
                <Col md={6}>
                    <FormGroup>
                        <Label for="created">
                            Kayıt Tarihi {createdAccess.required && <span className="text-danger">*</span>}
                        </Label>
                        <Input
                            type="datetime-local"
                            id="created"
                            name="created"
                            value={formData.customers?.[0]?.created instanceof Date 
                                ? formData.customers[0].created.toISOString().slice(0, 16) 
                                : formData.customers?.[0]?.created 
                                    ? new Date(formData.customers[0].created).toISOString().slice(0, 16)
                                    : ''}
                            disabled={true}
                            placeholder="Müşteri kayıt tarihi otomatik olarak atanır"
                            invalid={!!errors.created}
                        />
                        <FormFeedback>{errors.created}</FormFeedback>
                    </FormGroup>
                </Col>
            </Row>

            {formData.customerType === 'Bireysel' && (
                <Row>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="clientName">
                                Müvekkil Adı {clientNameAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="text"
                                id="clientName"
                                name="clientName"
                                value={formData.clientName || ''}
                                disabled={clientNameAccess.disabled}
                                onChange={(e) => {
                                    const value = normalizeFieldValue('clientName', e.target.value);
                                    handleInputChange({
                                        target: {
                                            name: 'clientName',
                                            value: value || null
                                        }
                                    });
                                }}
                                onBlur={(e) => {
                                    const finalValue = e.target.value.trim();
                                    if (finalValue !== formData.clientName) {
                                        handleInputChange({
                                            target: {
                                                name: 'clientName',
                                                value: finalValue || null
                                            }
                                        });
                                    }
                                }}
                                placeholder="AD SOYAD"
                                invalid={!!errors.clientName || (formData.clientName && !REGEX.FULL_NAME_UPPER_TR.test(formData.clientName.trim()))}
                            />
                            <FormFeedback>
                                {errors.clientName || 'Müvekkil adı tamamen büyük harflerden oluşmalı ve en az iki kelime içermelidir'}
                            </FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={6}>
                        <FormGroup>
                            <Label for="tcid">
                                TC Kimlik Numarası {tcidAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <InputGroup>
                                <Input
                                    type="text"
                                    id="tcid"
                                    name="tcid"
                                    value={formData.tcid || ''}
                                    disabled={tcidAccess.disabled}
                                    onChange={(e) => {
                                            const value = normalizeFieldValue('tcid', e.target.value);
                                        handleInputChange({
                                            target: {
                                                name: 'tcid',
                                                value: value || null
                                            }
                                        });
                                    }}
                                    maxLength={11}
                                    pattern="[0-9]{11}"
                                        invalid={!!errors.tcid || (formData.tcid && !validateTcid(formData.tcid))}
                                    placeholder="11 Haneli TC Kimlik No"
                                />
                                <FormFeedback style={{ display: errors.tcid || (formData.tcid && formData.tcid.length !== 11) ? 'block' : 'none' }}>
                                    {errors.tcid || (formData.tcid && formData.tcid.length !== 11 ? 'TC Kimlik Numarası 11 haneli olmalıdır' : '')}
                                </FormFeedback>
                            </InputGroup>
                        </FormGroup>
                    </Col>
                </Row>
            )}

            {formData.customerType === 'Kurumsal' && (
                <Row>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="companyName">
                                Şirket Adı {companyNameAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="text"
                                id="companyName"
                                name="companyName"
                                value={formData.companyName || ''}
                                disabled={companyNameAccess.disabled}
                                onChange={(e) => {
                                    handleInputChange({
                                        target: {
                                            name: 'companyName',
                                            value: e.target.value || null
                                        }
                                    });
                                }}
                                invalid={!!errors.companyName}
                            />
                            <FormFeedback>{errors.companyName}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="crsNumber">
                                MERSIS Numarası {crsNumberAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <Input
                                type="text"
                                id="crsNumber"
                                name="crsNumber"
                                value={formData.crsNumber || ''}
                                disabled={crsNumberAccess.disabled}
                                onChange={(e) => {
                                    const value = normalizeFieldValue('crsNumber', e.target.value);
                                    handleInputChange({
                                        target: {
                                            name: 'crsNumber',
                                            value: value || null
                                        }
                                    });
                                }}
                                invalid={!!errors.crsNumber}
                            />
                            <FormFeedback>{errors.crsNumber}</FormFeedback>
                        </FormGroup>
                    </Col>
                    <Col md={4}>
                        <FormGroup>
                            <Label for="taxNumber">
                                Vergi Numarası {taxNumberAccess.required && <span className="text-danger">*</span>}
                            </Label>
                            <InputGroup>
                                <Input
                                    type="text"
                                    id="taxNumber"
                                    name="taxNumber"
                                    value={formData.taxNumber || ''}
                                    disabled={taxNumberAccess.disabled}
                                    onChange={(e) => {
                                        const value = normalizeFieldValue('taxNumber', e.target.value);
                                        handleInputChange({
                                            target: {
                                                name: 'taxNumber',
                                                value: value || null
                                            }
                                        });
                                    }}
                                    maxLength={10}
                                    invalid={!!errors.taxNumber || (formData.taxNumber && !validateTaxNumber(formData.taxNumber))}
                                    placeholder="10 Haneli Vergi No"
                                />
                                <FormFeedback style={{ display: errors.taxNumber || (formData.taxNumber && formData.taxNumber.length !== 10) ? 'block' : 'none' }}>
                                    {errors.taxNumber || (formData.taxNumber && formData.taxNumber.length !== 10 ? 'Vergi Numarası 10 haneli olmalıdır' : '')}
                                </FormFeedback>
                            </InputGroup>
                        </FormGroup>
                    </Col>
                </Row>
            )}

            <FormGroup>
                <Label for="reference">Kimin Referansı? {referenceAccess.required && <span className="text-danger">*</span>}</Label>
                <Input
                    id="reference"
                    name="reference"
                    value={formData.reference || ''}
                    onChange={handleInputChange}
                    placeholder="Örn: Ahmet Yılmaz (Opsiyonel)"
                    disabled={referenceAccess.disabled}
                    required={referenceAccess.required}
                    invalid={!!errors.reference}
                />
                <FormFeedback>{errors.reference}</FormFeedback>
            </FormGroup>

            {['Evrak Bekleniyor', 'Eksper İşlem Bekleniyor', 'Yeni Eksper İşlem Bekleniyor', 'Eksper Rapor Bekleniyor', 'Yeni Eksper Rapor Bekleniyor', 'Eksper Raporu Ödeme Bekleniyor', 'Yeni Eksper Raporu Ödeme Bekleniyor', 'Sigorta Başvuru Dilekçe Bekleniyor', 'Yeni Sigorta Başvuru Dilekçe Bekleniyor', 'Ekspersiz Yeni Sigorta Başvuru Dilekçe Bekleniyor'].includes(callFormState) && (
                    <Row>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="licensePlate">
                                    Plaka {licensePlateAccess.required && <span className="text-danger">*</span>}
                                </Label>
                                <Input
                                    type="text"
                                    id="licensePlate"
                                    name="licensePlate"
                                    value={formData.licensePlate || ''}
                                    disabled={licensePlateAccess.disabled}
                                    onChange={(e) => {
                                        const value = normalizeFieldValue('licensePlate', e.target.value);
                                        handleInputChange({
                                            target: {
                                                name: 'licensePlate',
                                                value: value || null
                                            }
                                        });
                                    }}
                                    invalid={!!errors.licensePlate || !!(formData.licensePlate && !validateLicensePlate(formData.licensePlate))}
                                />
                                <FormFeedback>{errors.licensePlate || (formData.licensePlate && !validateLicensePlate(formData.licensePlate) ? 'Geçerli bir plaka giriniz (örn: 34 ABC 123).' : '')}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md={6}>
                            <FormGroup>
                                <Label for="accidentDate">
                                    Kaza Tarihi {accidentDateAccess.required && <span className="text-danger">*</span>}
                                </Label>
                                <Input
                                    type="datetime-local"
                                    id="accidentDate"
                                    name="accidentDate"
                                    value={formData.accidentDate instanceof Date && !isNaN(formData.accidentDate) 
                                        ? formData.accidentDate.toISOString().slice(0, 16) 
                                        : ''}
                                    disabled={accidentDateAccess.disabled}
                                    onChange={(e) => {
                                        const value = e.target.value ? new Date(e.target.value) : null;
                                        handleInputChange({
                                            target: {
                                                name: 'accidentDate',
                                                value
                                            }
                                        });
                                    }}
                                    invalid={!!errors.accidentDate}
                                />
                                <FormFeedback>{errors.accidentDate}</FormFeedback>
                            </FormGroup>
                        </Col>                                    </Row>
                )}
        </>
    );
};

CustomerInfoForm.propTypes = {
    callFormState: PropTypes.string.isRequired,
    formData: PropTypes.object.isRequired,
    errors: PropTypes.object.isRequired,
    handleInputChange: PropTypes.func.isRequired,
    fieldPermissions: PropTypes.array,
    canEdit: PropTypes.bool,
};

export default CustomerInfoForm;
